import { Dialog } from '@/components/Dialog';
import { DialogActions, ErrorMessage } from '@/components/Elements';
import { InputField, ToggleField } from '@/components/Form';
import { graphql, readFragment, useMutation } from '@/lib/graphql';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCaution } from './AlertCaution';

const savedFilterFragment = graphql(`
  fragment SavedFilterFragment on SavedFilter {
    id
    name
    alertEnabled
    condition {
      query
      themes
      countries
      countryTags
      organizationTypes
      topicTags
      publishedStartDate
      publishedEndDate
    }
    createdAt
    updatedAt
  }
`);

// フィルター名更新用のMutation
const updateSavedFilterMutation = graphql(
  `
    mutation UpdateSavedFilter($input: UpdateSavedFilterInput!) {
      updateSavedFilter(input: $input) {
        success
        savedFilter {
          ...SavedFilterFragment
        }
        affectedSavedFilters {
          ...SavedFilterFragment
        }
        userErrors {
          code
          message
          path
        }
      }
    }
  `,
  [savedFilterFragment]
);

type EditFilterNameModalProps = {
  isOpen: boolean;
  onClose: () => void;
  filterId: string;
  currentName: string;
  currentAlertEnabled: boolean;
  onSuccess?: () => void;
};

export const EditSavedFilterModal = ({
  isOpen,
  onClose,
  filterId,
  currentName,
  currentAlertEnabled,
}: EditFilterNameModalProps) => {
  const { t } = useTranslation();
  const [filterName, setFilterName] = useState(currentName);
  const [alertEnabled, setAlertEnabled] = useState(currentAlertEnabled);

  useEffect(() => {
    setFilterName(currentName);
    setAlertEnabled(currentAlertEnabled);
  }, [currentName, currentAlertEnabled]);

  const [updateSavedFilter, { data, loading }] = useMutation(
    updateSavedFilterMutation,
    {
      update: (cache, { data }) => {
        data?.updateSavedFilter?.affectedSavedFilters?.forEach(
          (affectedFilterRef) => {
            const affectedFilter = readFragment(
              savedFilterFragment,
              affectedFilterRef
            );
            if (!affectedFilter) return;

            cache.writeFragment({
              id: cache.identify({
                __typename: 'SavedFilter',
                id: affectedFilter.id,
              }),
              fragment: savedFilterFragment,
              data: affectedFilter,
            });
          }
        );
      },
    }
  );

  const handleUpdate = async () => {
    if (!filterName.trim()) return;

    const result = await updateSavedFilter({
      variables: {
        input: {
          id: filterId,
          name: filterName.trim(),
          alertEnabled,
        },
      },
    });

    if (result.data?.updateSavedFilter?.success) {
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('フィルタを編集')}
      maxWidth="lg"
    >
      <div className="max-h-[60vh] space-y-6 overflow-y-scroll">
        <InputField
          label={t('フィルタ名')}
          value={filterName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFilterName(e.target.value)
          }
          placeholder={t('フィルタ名を入力してください')}
          disabled={loading}
          required
        />

        {/* 通知設定 */}
        <div className="space-y-4">
          <ToggleField
            label={t('通知を受け取る')}
            checked={alertEnabled}
            onToggle={(checked) => setAlertEnabled(checked)}
            size="sm"
          />

          <div
            className={clsx(
              'overflow-hidden transition-all duration-300 ease-in-out',
              alertEnabled ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
            )}
          >
            <AlertCaution />
          </div>
        </div>

        {/* エラーメッセージ */}
        <ErrorMessage errors={data?.updateSavedFilter?.userErrors} />

        <DialogActions
          onCancel={handleCancel}
          onConfirm={handleUpdate}
          confirmText={loading ? t('更新中...') : t('更新')}
          isConfirmDisabled={loading || !filterName.trim()}
          isConfirmLoading={loading}
        />
      </div>
    </Dialog>
  );
};
